import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, messaging } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { getToken } from 'firebase/messaging';
import { Bell, Calendar, Clock, Save, Settings, CheckCircle, AlertCircle } from 'lucide-react';

type DaysOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6; // Sun = 0

interface NotificationScheduleForm {
  morningEnabled: boolean;
  morningTime: string; // HH:MM
  morningDays: DaysOfWeek[];
  eveningEnabled: boolean;
  eveningTime: string; // HH:MM
  eveningDays: DaysOfWeek[];
  incidentPromptsEnabled: boolean;
  reminderEnabled: boolean;
  reminderDelayMinutes: number;
  maxReminders: number;
  timezone: string;
}

const defaultSchedule = (): NotificationScheduleForm => ({
  morningEnabled: true,
  morningTime: '08:00',
  morningDays: [1, 2, 3, 4, 5],
  eveningEnabled: true,
  eveningTime: '20:00',
  eveningDays: [1, 2, 3, 4, 5],
  incidentPromptsEnabled: false,
  reminderEnabled: true,
  reminderDelayMinutes: 30,
  maxReminders: 2,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
});

export const JournalNotifications: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<NotificationScheduleForm>(defaultSchedule());
  const [permission, setPermission] = useState<NotificationPermission>(
    (typeof Notification !== 'undefined' ? Notification.permission : 'default') as NotificationPermission
  );
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const days = useMemo(
    () => [
      { label: 'Sun', value: 0 as DaysOfWeek },
      { label: 'Mon', value: 1 as DaysOfWeek },
      { label: 'Tue', value: 2 as DaysOfWeek },
      { label: 'Wed', value: 3 as DaysOfWeek },
      { label: 'Thu', value: 4 as DaysOfWeek },
      { label: 'Fri', value: 5 as DaysOfWeek },
      { label: 'Sat', value: 6 as DaysOfWeek }
    ],
    []
  );

  useEffect(() => {
    const fetch = async () => {
      if (!user) return;
      try {
        const ref = doc(db, 'users', user.uid, 'settings', 'journalNotifications');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as Partial<NotificationScheduleForm>;
          setForm({ ...defaultSchedule(), ...data });
        }
      } catch (err) {
        console.error('Failed to load notification settings', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [user]);

  const toggleDay = (key: 'morningDays' | 'eveningDays', value: DaysOfWeek) => {
    setForm((prev) => {
      const set = new Set(prev[key]);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, [key]: Array.from(set).sort() } as NotificationScheduleForm;
    });
  };

  const requestPermission = async () => {
    setTokenLoading(true);
    try {
      // Detect browser type for better error messages
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
      const isAndroid = /Android/.test(userAgent);
      const isMobile = isIOS || isAndroid;
      const isChrome = /Chrome/.test(userAgent) && !/Edg|OPR/.test(userAgent);
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      
      // Check if we're in a secure context (HTTPS required for notifications)
      const isSecureContext = window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost';
      
      // Check for basic notification support
      const hasNotificationSupport = typeof Notification !== 'undefined';
      
      // Check for service worker support (required for push notifications)
      const hasServiceWorker = 'serviceWorker' in navigator;
      
      // Debug logging
      console.log('🔍 Browser Detection:', {
        userAgent,
        isIOS,
        isAndroid,
        isMobile,
        isChrome,
        isSafari,
        isSecureContext,
        protocol: location.protocol,
        hostname: location.hostname,
        hasNotificationSupport,
        hasServiceWorker,
        Notification: typeof Notification,
        NotificationRequestPermission: typeof Notification?.requestPermission,
        NotificationPermission: Notification?.permission
      });
      
      // Check secure context first
      if (!isSecureContext) {
        console.error('❌ Not in secure context - HTTPS required for notifications');
        alert('Push notifications require HTTPS. Please access the app via HTTPS (https://gritgrowth-5b680.web.app) instead of HTTP.');
        setTokenLoading(false);
        return;
      }
      
      if (!hasNotificationSupport) {
        console.error('❌ Notification API not found');
        console.error('Window object:', window);
        console.error('Notification object:', window.Notification);
        alert('Your browser does not support notifications. Please use a modern browser like Chrome, Firefox, or Safari. If you are using Chrome, please update to the latest version.');
        setTokenLoading(false);
        return;
      }
      
      // On mobile, we need service worker for FCM, but let's still try to request permission
      // The actual FCM token request will fail if service worker isn't available
      if (!hasServiceWorker && isMobile) {
        console.warn('⚠️ Service Worker not available on mobile - push notifications may not work');
        // Don't block - still try to get permission, but warn about limited functionality
      }
      
      let grantedPermission = permission;
      
      // Request browser notification permission
      // Modern browsers: requestPermission() returns a Promise
      // Older browsers: requestPermission(callback) uses a callback
      // iOS Safari: requestPermission() exists but may work differently
      
      try {
        console.log('🔔 Requesting notification permission...');
        
        // Check if requestPermission exists
        if (!Notification.requestPermission) {
          throw new Error('Notification.requestPermission is not available. Your browser may not support web notifications.');
        }
        
        // Modern browsers - requestPermission() returns a Promise
        const requestResult = Notification.requestPermission();
        
        if (requestResult instanceof Promise) {
          // Modern API - returns a promise
          console.log('📱 Using Promise-based permission request');
          grantedPermission = await requestResult;
        } else if (typeof requestResult === 'string') {
          // Some browsers return the result directly (deprecated)
          console.log('📱 Using direct permission result');
          grantedPermission = requestResult as NotificationPermission;
        } else {
          // Fallback: Check current permission state
          console.log('📱 Checking current permission state');
          grantedPermission = Notification.permission || 'default';
          
          if (grantedPermission === 'default') {
            // Try callback-based approach for very old browsers
            await new Promise<void>((resolve, reject) => {
              try {
                (Notification.requestPermission as any)((perm: NotificationPermission) => {
                  grantedPermission = perm;
                  resolve();
                });
              } catch (err) {
                reject(err);
              }
            });
          }
        }
        
        console.log('✅ Permission result:', grantedPermission);
      } catch (error: any) {
        console.error('Error requesting notification permission:', error);
        
        // Provide helpful error messages based on platform
        if (isIOS) {
          const isStandalone = (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
          if (!isStandalone) {
            alert('On iOS Safari, push notifications only work when the app is installed as a Progressive Web App (PWA).\n\nTo install:\n1. Tap the Share button (square with arrow)\n2. Select "Add to Home Screen"\n3. Open the app from your home screen\n4. Then try enabling notifications again.');
            setTokenLoading(false);
            return;
          }
        }
        
        alert('Unable to request notification permission. Please try again or enable notifications in your browser settings.');
        setTokenLoading(false);
        return;
      }
      
      setPermission(grantedPermission);
      
      // hint vibration when granted
      // @ts-expect-error
      navigator.vibrate?.(30);
      
      if (grantedPermission !== 'granted') {
        if (grantedPermission === 'denied') {
          alert('Notification permission was denied. Please enable notifications in your browser settings:\n\nChrome/Edge: Settings → Site Settings → Notifications\nSafari: Settings → Safari → Notifications\nFirefox: Click the padlock icon → Permissions → Notifications');
        } else {
          alert('Notification permission was not granted. Please try again and allow notifications when prompted.');
        }
        setTokenLoading(false);
        return;
      }
      
      // Check if messaging is available - wait a bit if it's still initializing
      let messagingInstance = messaging;
      if (!messagingInstance) {
        // Wait up to 2 seconds for messaging to initialize
        for (let i = 0; i < 20; i++) {
          await new Promise(resolve => setTimeout(resolve, 100));
          messagingInstance = messaging;
          if (messagingInstance) break;
        }
      }
      
      if (!messagingInstance) {
        console.warn('⚠️ Firebase Messaging not initialized');
        console.warn('Check if VITE_FIREBASE_MESSAGING_SENDER_ID is set in .env');
        alert('Push notifications are not available. Please refresh the page and try again. If the issue persists, check your browser console for details.');
        setTokenLoading(false);
        return;
      }
      
      // Then request FCM token if permission was granted
      if (grantedPermission === 'granted') {
        try {
          const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
          if (!vapidKey) {
            console.error('⚠️ VAPID key not configured');
            alert('Push notifications are not configured. Please contact support.');
            setTokenLoading(false);
            return;
          }
          
          console.log('🔑 Requesting FCM token...');
          const currentToken = await getToken(messagingInstance, { 
            vapidKey: vapidKey
          });
          
          if (currentToken) {
            console.log('✅ FCM token received:', currentToken.substring(0, 20) + '...');
            setFcmToken(currentToken);
            
            // Save token to Firestore
            if (user) {
              const tokenRef = doc(db, 'users', user.uid, 'settings', 'fcmToken');
              await setDoc(tokenRef, { 
                token: currentToken,
                updatedAt: new Date()
              }, { merge: true });
              console.log('✅ FCM token saved to Firestore');
              alert('✅ Push notifications enabled! You will receive reminders for your journal check-ins.');
            }
          } else {
            console.warn('⚠️ No FCM token available');
            alert('Unable to get push notification token. Please check your browser settings and try again.');
          }
        } catch (err: any) {
          console.error('❌ Failed to get FCM token:', err);
          let errorMessage = 'Failed to enable push notifications. ';
          
          if (err.code === 'messaging/permission-blocked') {
            errorMessage += 'Notifications are blocked. Please enable them in your browser settings.';
          } else if (err.code === 'messaging/unsupported-browser') {
            errorMessage += 'Your browser does not support push notifications.';
          } else {
            errorMessage += err.message || 'Please try again later.';
          }
          
          alert(errorMessage);
        }
      }
    } catch (err: any) {
      console.error('❌ Notification permission request failed:', err);
      alert('Failed to enable notifications: ' + (err.message || 'Unknown error'));
    } finally {
      setTokenLoading(false);
    }
  };

  const save = async () => {
    if (!user) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const ref = doc(db, 'users', user.uid, 'settings', 'journalNotifications');
      await setDoc(ref, { 
        ...form,
        updatedAt: new Date()
      }, { merge: true });
      
      // If notifications are enabled but no FCM token, request it
      if ((form.morningEnabled || form.eveningEnabled) && !fcmToken && messaging) {
        await requestPermission();
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save notification settings', err);
    } finally {
      setSaving(false);
    }
  };

  // Check for existing FCM token on mount and verify messaging initialization
  useEffect(() => {
    const checkMessagingAndToken = async () => {
      if (!user) return;
      
      // Wait a bit for messaging to initialize
      let attempts = 0;
      while (!messaging && attempts < 20) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!messaging) {
        console.warn('⚠️ Firebase Messaging not available');
        return;
      }
      
      // Update permission state from browser
      if (typeof Notification !== 'undefined') {
        setPermission(Notification.permission);
      }
      
      // Check for existing token if permission is granted
      if (permission === 'granted' || Notification.permission === 'granted') {
        try {
          const tokenRef = doc(db, 'users', user.uid, 'settings', 'fcmToken');
          const tokenSnap = await getDoc(tokenRef);
          if (tokenSnap.exists()) {
            const tokenData = tokenSnap.data();
            setFcmToken(tokenData.token || null);
          }
        } catch (err) {
          console.error('Failed to load FCM token:', err);
        }
      }
    };
    
    checkMessagingAndToken();
  }, [user]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-6 w-40 bg-gray-200 rounded" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-50 p-3 rounded-xl">
            <Bell className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Notification Preferences</h1>
            <p className="text-gray-600">Control your journal reminders</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {saveSuccess && (
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Saved!</span>
            </div>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving…' : 'Save'}</span>
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <div className="font-medium">Morning Prompt</div>
                <div className="text-sm text-gray-500">A gentle nudge to start your day</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={form.morningEnabled}
                onChange={(e) => setForm({ ...form, morningEnabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-5 after:w-5 after:rounded-full after:transition-all peer-checked:after:translate-x-full" />
            </label>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input
              type="time"
              value={form.morningTime}
              onChange={(e) => setForm({ ...form, morningTime: e.target.value })}
              className="border rounded-lg px-3 py-2"
            />
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              {days.map((d) => (
                <button
                  key={`m-${d.value}`}
                  onClick={() => toggleDay('morningDays', d.value)}
                  className={`px-3 py-1.5 rounded-full border ${form.morningDays.includes(d.value) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700'}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-gray-600" />
              <div>
                <div className="font-medium">Evening Prompt</div>
                <div className="text-sm text-gray-500">Reflect and wrap your day</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={form.eveningEnabled}
                onChange={(e) => setForm({ ...form, eveningEnabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-5 after:w-5 after:rounded-full after:transition-all peer-checked:after:translate-x-full" />
            </label>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input
              type="time"
              value={form.eveningTime}
              onChange={(e) => setForm({ ...form, eveningTime: e.target.value })}
              className="border rounded-lg px-3 py-2"
            />
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              {days.map((d) => (
                <button
                  key={`e-${d.value}`}
                  onClick={() => toggleDay('eveningDays', d.value)}
                  className={`px-3 py-1.5 rounded-full border ${form.eveningDays.includes(d.value) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700'}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Settings className="w-5 h-5 text-gray-600" />
              <div>
                <div className="font-medium">Reminders</div>
                <div className="text-sm text-gray-500">Follow-ups if you miss a prompt</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={form.reminderEnabled}
                onChange={(e) => setForm({ ...form, reminderEnabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-5 after:w-5 after:rounded-full after:transition-all peer-checked:after:translate-x-full" />
            </label>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="text-sm text-gray-700">
              Delay minutes
              <input
                type="number"
                min={5}
                max={180}
                value={form.reminderDelayMinutes}
                onChange={(e) => setForm({ ...form, reminderDelayMinutes: Number(e.target.value) })}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </label>
            <label className="text-sm text-gray-700">
              Max reminders
              <input
                type="number"
                min={0}
                max={5}
                value={form.maxReminders}
                onChange={(e) => setForm({ ...form, maxReminders: Number(e.target.value) })}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </label>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Push Notification Setup</div>
                <div className="text-sm text-gray-500">
                  Browser permission: <span className="font-medium">{permission}</span>
                  {fcmToken && (
                    <span className="ml-2 text-green-600 flex items-center space-x-1">
                      <CheckCircle className="w-3 h-3 inline" />
                      <span>Notifications Saved</span>
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={requestPermission}
                disabled={tokenLoading}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {tokenLoading ? 'Setting up...' : permission === 'granted' && fcmToken ? 'Enabled ✓' : 'Enable Notifications'}
              </button>
            </div>
            {permission === 'denied' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Notifications are blocked</p>
                  <p>Please enable notifications in your browser settings to receive journal reminders.</p>
                </div>
              </div>
            )}
            {!fcmToken && permission === 'granted' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p>Click "Enable Notifications" above to register your device for push notifications.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalNotifications;



